/**
 * Assessment Rank Display Component
 * 
 * Displays organization or value assessment rank (1-10) with
 * description and improvement recommendations modal.
 */

import React, { useState } from 'react';
import { CardSectionProps } from '../../../types/compositeCards';
import { Modal } from 'antd';
import { 
  TrophyOutlined, 
  StarOutlined, 
  InfoCircleOutlined,
  BulbOutlined 
} from '@ant-design/icons';

interface AssessmentData {
  assessment_rank: number; // 0-100
  rank_category: string; // e.g., "Excellent", "Good", etc.
  reasoning: string;
  improvements: string[];
}

export const AssessmentRankDisplay: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const [showImprovementModal, setShowImprovementModal] = useState(false);
  const { data } = card;

  // Extract assessment data from artifact_item metadata or card data
  const itemMetadata = (data as any)?.item_metadata || (data as any)?.metadata || {};
  const assessment: AssessmentData = {
    assessment_rank: itemMetadata.assessment_rank ?? (data as any)?.assessmentRank?.score ? (data as any).assessmentRank.score * 10 : 70,
    rank_category: itemMetadata.rank_category ?? (data as any)?.assessmentRank?.category ?? 'Good',
    reasoning: itemMetadata.rank_reasoning ?? (data as any)?.assessmentRank?.description ?? 'Assessment analysis in progress.',
    improvements: itemMetadata.rank_improvements ?? (data as any)?.assessmentRank?.improvements ?? []
  };
  
  // Convert 0-100 scale to 0-10 for display (keeping backward compatibility)
  const displayScore = Math.round(assessment.assessment_rank / 10);

  const getScoreColor = (rank: number): string => {
    // Use 0-100 scale
    if (rank >= 80) return '#10b981'; // Green
    if (rank >= 60) return '#f59e0b'; // Orange
    if (rank >= 40) return '#ef4444'; // Red
    return '#dc2626'; // Dark red
  };

  const getScoreLabel = (rank: number, category?: string): string => {
    // Use provided category if available, otherwise calculate from rank
    if (category) return category;
    if (rank >= 90) return 'Excellent';
    if (rank >= 80) return 'Very Good';
    if (rank >= 70) return 'Good';
    if (rank >= 60) return 'Above Average';
    if (rank >= 50) return 'Average';
    if (rank >= 40) return 'Below Average';
    if (rank >= 30) return 'Needs Improvement';
    if (rank >= 20) return 'Poor';
    if (rank >= 10) return 'Very Poor';
    return 'Critical';
  };

  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid #e5e7eb',
      borderBottom: '1px solid #e5e7eb'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrophyOutlined style={{ fontSize: '20px', color: getScoreColor(assessment.assessment_rank) }} />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
            Assessment Rank
          </h3>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            color: getScoreColor(assessment.assessment_rank)
          }}>
            {assessment.assessment_rank}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280'
          }}>
            / 100
          </div>
        </div>
      </div>

      {/* Score Label and Stars */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <div style={{
          padding: '4px 12px',
          backgroundColor: getScoreColor(assessment.assessment_rank) + '15',
          color: getScoreColor(assessment.assessment_rank),
          borderRadius: '12px',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          {getScoreLabel(assessment.assessment_rank, assessment.rank_category)}
        </div>
        
        <div style={{ display: 'flex', gap: '2px' }}>
          {[...Array(10)].map((_, i) => (
            <StarOutlined 
              key={i} 
              style={{ 
                fontSize: '12px',
                color: i < displayScore ? getScoreColor(assessment.assessment_rank) : '#d1d5db'
              }} 
            />
          ))}
        </div>
      </div>

      {/* Reasoning */}
      <p style={{
        margin: '0 0 16px 0',
        fontSize: '14px',
        color: '#4b5563',
        lineHeight: '1.5'
      }}>
        {assessment.reasoning}
      </p>

      {/* How to Improve Button */}
      <button
        onClick={() => setShowImprovementModal(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          backgroundColor: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s',
          width: '100%',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#2563eb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#3b82f6';
        }}
      >
        <BulbOutlined />
        How to Improve
      </button>

      {/* Improvement Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BulbOutlined style={{ color: '#f59e0b', fontSize: '20px' }} />
            <span>Improvement Recommendations</span>
          </div>
        }
        open={showImprovementModal}
        onOk={() => setShowImprovementModal(false)}
        onCancel={() => setShowImprovementModal(false)}
        footer={[
          <button
            key="close"
            onClick={() => setShowImprovementModal(false)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Got it
          </button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            To improve your assessment rank to {Math.min(100, assessment.assessment_rank + 10)} or higher, focus on:
          </p>
          
          <ul style={{
            margin: 0,
            padding: '0 0 0 20px',
            listStyle: 'none'
          }}>
            {assessment.improvements.map((improvement, index) => (
              <li key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '12px',
                fontSize: '14px',
                color: '#374151'
              }}>
                <InfoCircleOutlined style={{ 
                  color: '#3b82f6', 
                  fontSize: '16px',
                  marginTop: '2px'
                }} />
                <span style={{ flex: 1 }}>{improvement}</span>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </div>
  );
};

